import * as React from "react";
import { styled } from "@mui/material/styles";
import {
  Grid,
  Typography,
  Link,
  List,
  ListItem,
  ListItemIcon,
  FormControl,
  Select,
  Box,
  Button,
} from "@mui/material";
import { AppBody } from "../AppBody";
import Breadcrumbs from "../../containers/Breadcrumbs";
import {
  appHeaderTitle,
  fileNameFormat,
} from "../../common/services/Utilities";
import { useInitialPageLoad } from "../../hooks/useInitialPageLoad";
import CloudFoundryBanner from "../../containers/CloudFoundryBanner";
import BackButton from "../../containers/BackButton";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { KeyStringValue, KeyValue } from "../../interfaces";
import { fetchData } from "../../common/services/ApiClient";
import { ApiBaseUrl, AppUrl } from "../../config";
import LoadingIndicator from "../LoadingIndicator";

const PREFIX = "BulkDataDownload";
const HISTORY_MAX_YEARS = 10;

const classes = {
  MuiSvgIcon: `${PREFIX}-MuiSvgIcon`,
  detailsSearchForm: `${PREFIX}-detailsSearchForm`,
  searchPanelHeadings: `${PREFIX}-searchPanelHeadings`,
  button: `${PREFIX}-button`,
  footer: `${PREFIX}-footer`,
  selectError: `${PREFIX}-selectError`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.MuiSvgIcon}`]: {
    color: theme.palette.primary.main,
    fontSize: "10px",
  },

  [`& .${classes.detailsSearchForm}`]: {
    width: "fit-content",
    minWidth: "100%",
  },

  [`& .${classes.searchPanelHeadings}`]: {
    color: "#fff",
    fontWeight: "bold",
    padding: 0,
  },

  [`& .${classes.button}`]: {
    "fontSize": "1rem",
    "padding": 0,
    "fontWeight": 400,
    "margin": 0,
    "width": 240,
    "height": 32,
    "&#download-data-btn:hover": {
      backgroundColor: theme.palette.primary.main,
      color: "#FFFFFF",
      borderColor: theme.palette.primary.main,
      boxShadow: theme.shadows[1],
    },
  },

  [`& .${classes.footer}`]: {
    color: theme.palette.primary.main,
  },

  [`& .${classes.selectError}`]: {
    "&.Mui-error .MuiOutlinedInput-notchedOutline": {
      borderColor: "#FF0000",
      borderWidth: "2px",
    },
  },
}));

export default function BulkDataDownload() {
  useInitialPageLoad("bulkDataDownload");

  const definitionLinks: KeyStringValue[] = [
    {
      name: "Institutions Definitions (CSV format)",
      url: `${AppUrl("API")}/institutions_definitions.csv`,
      description: "Definitions of fields in Institutions data.",
    },
    {
      name: "Locations Definitions (CSV format)",
      url: `${AppUrl("API")}/locations_definitions.csv`,
      description: "Definitions of fields in Locations data",
    },
    {
      name: "History Events Definitions (CSV format)",
      url: `${AppUrl("API")}/events_definitions.csv`,
      description: "Definitions of fields in History data.",
    },
    {
      name: "SOD Definitions (CSV format)",
      url: `${AppUrl("API")}/sod_variables_definitions.csv`,
      description: "Definitions of fields in Summary of Deposits data.",
    },
  ];
  const bulkLinks: KeyStringValue[] = [
    {
      name: "Institutions (CSV format)",
      url: "https://s3-us-gov-west-1.amazonaws.com/cg-2e5c99a6-e282-42bf-9844-35f5430338a5/downloads/institutions.csv",
      description:
        "Listing of FDIC-insured banking institution demographic and headquarter location data",
    },
    {
      name: "Locations (CSV format)",
      url: "https://s3-us-gov-west-1.amazonaws.com/cg-2e5c99a6-e282-42bf-9844-35f5430338a5/downloads/locations.csv",
      description: "Listing of FDIC-insured bank branches and locations",
    },
    {
      name: "Links to Create Common Financial Reports (.xlsx spreadsheet)",
      url: `${AppUrl("API")}/All Financial Reports.xlsx`,
    },
    {
      name: "RIS Data Bulk Download",
      url: "https://www.fdic.gov/foia/ris-data-bulk-download",
    },
    {
      name: "Legacy SDI ZIP files ",
      url: "https://www.fdic.gov/foia/quarterly-financial-reports-statistics-depository-institutions-sdi",
    },
    {
      name: "SOD Data Files from 1987 to 1993",
      url: "https://www.fdic.gov/foia/sod/",
    },
  ];

  const reportTypes = [
    { title: "Financial" },
    { title: "Institutions" },
    { title: "Locations" },
    { title: "Historical (Structure Changes)" },
    { title: "Failures" },
    { title: "Summary (Historical Data Aggregations)" },
    { title: "SOD (Summary of Deposits)" },
  ];

  const getYearsDescending = (min: number, max: number) => {
    const years = [];
    for (let year = max; year >= min; year--) {
      years.push(year);
    }
    return years;
  };
  const [formValues, setFormValues] = React.useState<{
    reportType: string;
    quarter: string;
    startYear: string;
    endYear: string;
    format: string;
    year: string;
  }>({
    reportType: "",
    quarter: "",
    startYear: "",
    endYear: "",
    format: "CSV (comma separated values)",
    year: "",
  });

  const earliestYearLookup = (): number => {
    const lookup: KeyValue = {
      "Historical (Structure Changes)": 1999,
      "Summary (Historical Data Aggregations)": 1934,
      "SOD (Summary of Deposits)": 1994,
    };
    return Object.hasOwn(lookup, formValues.reportType)
      ? lookup[formValues.reportType]
      : 1992;
  };
  const [risdate, setRisdate] = React.useState(2024);
  const [risQuarter, setRisQuarter] = React.useState("1231");
  const [sodDate, setSodDate] = React.useState();
  const useSodDate =
    sodDate && formValues.reportType === "SOD (Summary of Deposits)";
  const yearChoice = getYearsDescending(
    earliestYearLookup(),
    useSodDate ? sodDate : risdate,
  );

  const isHistory = formValues.reportType === "Historical (Structure Changes)";
  const startNum = parseInt(formValues.startYear);
  const endNum = parseInt(formValues.endYear);

  const startYearChoice =
    isHistory && formValues.endYear
      ? yearChoice.filter(
          (year) => year <= endNum && year > endNum - HISTORY_MAX_YEARS,
        )
      : yearChoice;

  const endYearChoice =
    isHistory && formValues.startYear
      ? yearChoice.filter(
          (year) => year >= startNum && year < startNum + HISTORY_MAX_YEARS,
        )
      : yearChoice;

  const quarterChoices = [
    { value: "0331", label: "Q1 - ending 03/31" },
    { value: "0630", label: "Q2 - ending 06/30" },
    { value: "0930", label: "Q3 - ending 09/30" },
    { value: "1231", label: "Q4 - ending 12/31" },
  ];
  // if current year is selected, trim quarter options to most recent quarter
  if (
    formValues.reportType === "Financial" &&
    formValues.endYear === risdate.toString()
  ) {
    risQuarter === "0331" && quarterChoices.splice(1, 3);
    risQuarter === "0630" && quarterChoices.splice(2, 2);
    risQuarter === "0930" && quarterChoices.splice(3, 1);
  }

  const handleChange = (field: string) => (event: any) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const [dataLoading, setDataLoading] = React.useState<boolean>(false);

  React.useEffect(() => {
    let isMounted = true;
    (async () => {
      await fetchData(
        `${ApiBaseUrl()}/financials?limit=1&offset=0&sort_by=RISDATE&sort_order=DESC&fields=RISDATE`,
      )
        .then((response: any) => {
          if (isMounted && response.data) {
            setRisdate(parseInt(response?.data?.[0]?.RISDATE?.substring(0, 4)));
            setRisQuarter(response?.data?.[0]?.RISDATE?.substring(4, 8));
          }
        })
        .catch((error: Error) => {
          console.error(`${error.name} : ${error.message}`);
        })
        .finally(() => {
          isMounted = false;
        });
    })();
  }, []);

  React.useEffect(() => {
    let isMounted = true;
    if (formValues.reportType === "SOD (Summary of Deposits)" && !sodDate) {
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/sod?agg_by=YEAR&sort_by=YEAR&sort_order=desc&agg_limit=1`,
        )
          .then((response: any) => {
            if (isMounted && response.data) {
              setSodDate(response.data[0].YEAR);
            }
          })
          .catch((error: Error) => {
            console.error(`${error.name} : ${error.message}`);
          })
          .finally(() => {
            isMounted = false;
          });
      })();
    }
  }, [formValues.reportType, sodDate]);

  const downloadDataAsFile = (
    data: any[],
    format: string,
    filename: string,
  ): void => {
    const downloadData = Array.isArray(data) ? data : [];

    if (format === "CSV (comma separated values)") {
      const headersSet = new Set<string>();
      downloadData.forEach((row) => {
        if (row && typeof row === "object") {
          Object.keys(row).forEach((k) => headersSet.add(k));
        }
      });

      const headers =
        headersSet.size > 0 ? Array.from(headersSet) : ["Column1"];

      const csvContent = [
        headers.join(","),
        ...downloadData.map((row) =>
          headers
            .map(
              (field) =>
                `"${(row?.[field] ?? "").toString().replace(/"/g, '""')}"`,
            )
            .join(","),
        ),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = `${filename}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } else {
      const flatData =
        Array.isArray(downloadData) &&
        downloadData.every((r) => typeof r === "object")
          ? downloadData.flatMap((obj: any) => obj?.data ?? obj)
          : [];

      const jsonStr = JSON.stringify(flatData, null, 2);
      const blob = new Blob([jsonStr], { type: "application/json" });
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = `${filename}.json`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  React.useEffect(() => {
    setFormValues((prev) => ({
      ...prev,
      startYear: "",
      endYear: "",
      quarter: "",
      year: "",
    }));
  }, [formValues.reportType]);

  React.useEffect(() => {
    const { reportType, startYear, endYear, quarter, format, year } =
      formValues;

    if (!dataLoading) return;

    let isMounted = true;

    const fetchAndDownload = async () => {
      const requests = [];

      if (reportType === "Financial" && endYear && quarter) {
        const risdate = `"${endYear}${quarter}"`;

        const dataSize = await fetchData(
          `${ApiBaseUrl()}/financials?limit=1&filters=RISDATE:${risdate}`,
        ).then((res) => res.totals.count);

        const numberOfRequests = Math.ceil(dataSize / 500);

        for (let i = 0; i < numberOfRequests; i++) {
          requests.push(
            `${ApiBaseUrl()}/financials?limit=500&offset=${
              i * 500
            }&filters=RISDATE:${risdate} AND !BKCLASS:NC AND !BKCLASS:NS AND !BKCLASS:CU&sort_by=CERT&sort_order=asc`,
          );
        }
      } else if (reportType === "Historical (Structure Changes)") {
        const dateRange =
          startYear === "1999"
            ? `PROCDATE:[8/1/1999 TO 12/31/${endYear}]`
            : `PROCDATE:[1/1/${startYear} TO 12/31/${endYear}]`;
        const dataSize = await fetchData(
          `${ApiBaseUrl()}/history?filters=${dateRange}&limit=1&fields=PROCDATE`,
        ).then((res) => res.totals.count);

        const numberOfRequests = Math.ceil(dataSize / 1000);
        for (let i = 0; i < numberOfRequests; i++) {
          requests.push(
            `${ApiBaseUrl()}/history?filters=${dateRange}&limit=1000&offset=${
              i * 1000
            }`,
          );
        }
      } else if (reportType === "Failures" && startYear && endYear) {
        const dataSize = await fetchData(
          `${ApiBaseUrl()}/failures?filters=FAILYR:["${startYear}" TO "${endYear}"]&limit=1`,
        ).then((res) => res.totals.count);

        const numberOfRequests = Math.ceil(dataSize / 500);

        for (let i = 0; i < numberOfRequests; i++) {
          requests.push(
            `${ApiBaseUrl()}/failures?filters=FAILYR:["${startYear}" TO "${endYear}"]&limit=500&offset=${
              i * 500
            }`,
          );
        }
      } else if (reportType === "Summary (Historical Data Aggregations)") {
        const dateRange = `YEAR:[${startYear} TO ${endYear}]`;
        const dataSize = await fetchData(
          `${ApiBaseUrl()}/summary?filters=${dateRange}&limit=1&fields=YEAR`,
        ).then((res) => res.totals.count);

        const numberOfRequests = Math.ceil(dataSize / 10000);

        for (let i = 0; i < numberOfRequests; i++) {
          requests.push(
            `${ApiBaseUrl()}/summary?filters=${dateRange}&limit=10000&offset=${
              i * 10000
            }`,
          );
        }
      } else if (reportType === "SOD (Summary of Deposits)" && year) {
        const dataSize = await fetchData(
          `${ApiBaseUrl()}/sod?filters=YEAR:${year}&limit=1&fields=YEAR`,
        ).then((res) => res.totals.count);

        const numberOfRequests = Math.ceil(dataSize / 10000);

        for (let i = 0; i < numberOfRequests; i++) {
          requests.push(
            `${ApiBaseUrl()}/sod?filters=YEAR:${year}&limit=10000&offset=${
              i * 10000
            }`,
          );
        }
      } else if (reportType === "Institutions") {
        const dataSize = await fetchData(
          `${ApiBaseUrl()}/institutions?limit=1`,
        ).then((res) => res.totals.count);

        const numberOfRequests = Math.ceil(dataSize / 500);

        for (let i = 0; i < numberOfRequests; i++) {
          requests.push(
            `${ApiBaseUrl()}/institutions?limit=500&offset=${
              i * 500
            }&sort_by=CERT&sort_order=asc`,
          );
        }
      } else if (reportType === "Locations") {
        const dataSize = await fetchData(
          `${ApiBaseUrl()}/locations?limit=1`,
        ).then((res) => res.totals.count);

        const numberOfRequests = Math.ceil(dataSize / 500);

        for (let i = 0; i < numberOfRequests; i++) {
          requests.push(
            `${ApiBaseUrl()}/locations?limit=500&offset=${
              i * 500
            }&sort_by=CERT&sort_order=asc`,
          );
        }
      }

      const res: any[] = await Promise.all(
        requests.map((url) => fetchData(url)),
      ).then((resObj) => resObj.map((obj) => obj.data).flat());

      if (isMounted && res.length >= 0) {
        const filename = fileNameFormat(reportType);
        downloadDataAsFile(res, format, filename);
      }
    };

    fetchAndDownload().finally(() => {
      setDataLoading(false);
    });

    return () => {
      isMounted = false;
    };
  }, [
    dataLoading,
    formValues,
    formValues.reportType,
    formValues.startYear,
    formValues.endYear,
    formValues.quarter,
    formValues.format,
  ]);

  const dateRange = (
    <Typography className={classes.searchPanelHeadings} variant="body2">
      Date Range of Data
    </Typography>
  );

  const bulletPoints: string[] = [
    "Due to their size, financial downloads are limited to a single quarter",
    "Summary of Deposits downloads are limited to a single year",
    "Structure-related downloads are limited to the current (weekly) update",
  ];

  const isFormIncomplete =
    !formValues.reportType ||
    !formValues.format ||
    (["Institutions", "Locations"].includes(formValues.reportType)
      ? false
      : Object.values(
          formValues.reportType === "Financial"
            ? { quarter: formValues.quarter }
            : formValues.reportType === "SOD (Summary of Deposits)"
              ? { year: formValues.year }
              : {
                  startYear: formValues.startYear,
                  endYear: formValues.endYear,
                },
        ).some((value) => !value));

  const introParagraph = (
    <>
      {appHeaderTitle("bulkDataDownload")}
      <Typography variant="body2" style={{ maxWidth: 1075 }}>
        Bulk data download is an easy way to get large amounts of data in your
        choice of formats. The caveat is that it is less customizable than using
        the API and has the following limitations:
      </Typography>
      <List style={{ padding: 0 }}>
        {bulletPoints.map((text, index) => (
          <ListItem style={{ paddingTop: 0 }} key={index}>
            <ListItemIcon style={{ minWidth: 20 }}>
              <FiberManualRecordIcon className={classes.MuiSvgIcon} />
            </ListItemIcon>
            <Typography
              component="span"
              variant={"body2"}
              style={{ paddingBottom: 0 }}
            >
              {text}
            </Typography>
          </ListItem>
        ))}
      </List>
      <Typography variant="body2" id="bulk-data-api-link">
        Provide{" "}
        <Link
          href={
            "https://ask.fdic.gov/fdicinformationandsupportcenter/s/fdicdirform?app=FinRegData"
          }
        >
          feedback or submit a question
        </Link>{" "}
        about this page.
      </Typography>
    </>
  );

  const footerLookup: KeyValue = {
    "Financial":
      "Financial downloads are very large, so each download is limited to a single quarter and not the full set of RIS variables. You will, however, get every institution reporting in that time frame.",
    "Institutions":
      "Institutions and Locations endpoints are updated weekly and limited to the current data.",
    "Locations":
      "Institutions and Locations endpoints are updated weekly and limited to the current data.",
    "SOD (Summary of Deposits)":
      "Summary of Deposits downloads are limited to a single year.",
    "Historical (Structure Changes)":
      "History downloads are very large, so each download is limited to 10 years.",
  };

  const footer = Object.hasOwn(footerLookup, formValues.reportType) ? (
    <Typography className={classes.footer}>
      <b>Note:</b> {footerLookup[formValues.reportType]}
    </Typography>
  ) : (
    <></>
  );

  return (
    <Root>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Breadcrumbs currentPageBreadCrumbTitle="Bulk Data Download" />
        </Grid>
      </Grid>
      <CloudFoundryBanner />
      <AppBody
        introText={introParagraph}
        backButton={
          <BackButton
            app={"BulkData"}
            buttonLabel="Back to Previous"
            variant="dark"
          />
        }
      >
        <Typography
          variant="h3"
          sx={{
            paddingTop: 2,
          }}
        >
          Bulk Data Generator
        </Typography>
        <Box
          className={classes.detailsSearchForm}
          sx={{
            bgcolor: "primary.main",
            padding: 1,
          }}
        >
          <Grid container spacing={1}>
            <Grid
              size={{
                sm: 3,
              }}
            >
              <Typography
                className={classes.searchPanelHeadings}
                variant="body2"
              >
                Data Type - API Endpoint
              </Typography>
              <FormControl
                variant="outlined"
                fullWidth
                size="small"
                margin="dense"
              >
                <Select
                  inputProps={{ "aria-label": "Select a Report Type" }}
                  native
                  labelId={`select-report-type-label`}
                  id={`select-report-type`}
                  value={formValues.reportType}
                  onChange={handleChange("reportType")}
                >
                  <option value="">Make a Selection</option>
                  {reportTypes.map((value: KeyValue, index: number) => {
                    const int = (index + 1).toString();

                    return (
                      <option key={int} value={value.title}>
                        {value.title}
                      </option>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
            {formValues.reportType === "Institutions" ||
            formValues.reportType === "Locations" ? (
              <Grid
                size={{
                  sm: 6,
                }}
              >
                {dateRange}
                <Typography
                  variant="body2"
                  sx={{
                    color: "#fff",
                    textAlign: "center",
                    paddingTop: 2,
                  }}
                >
                  Structural data is only available for the current week.
                </Typography>
              </Grid>
            ) : formValues.reportType === "SOD (Summary of Deposits)" ? (
              <Grid
                size={{
                  sm: 6,
                }}
              >
                {dateRange}
                <FormControl
                  variant="outlined"
                  fullWidth
                  size="small"
                  margin="dense"
                >
                  <Select
                    inputProps={{ "aria-label": "Select Year" }}
                    native
                    labelId={`select-year`}
                    id={`select-year`}
                    value={formValues.year}
                    onChange={handleChange("year")}
                  >
                    <option value="">Select Year</option>
                    {yearChoice.map((e: number, index: number) => {
                      const int = (index + 1).toString();

                      return (
                        <option key={int} value={e}>
                          {e}
                        </option>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
            ) : (
              <>
                <Grid
                  size={{
                    sm: 3,
                  }}
                >
                  {dateRange}
                  <FormControl
                    variant="outlined"
                    fullWidth
                    size="small"
                    margin="dense"
                    disabled={formValues.reportType === ""}
                  >
                    {formValues.reportType === "Financial" ? (
                      <Select
                        inputProps={{ "aria-label": "Select a Quarter" }}
                        native
                        labelId={`select-quarter`}
                        id={`select-quarter`}
                        value={formValues.quarter}
                        onChange={handleChange("quarter")}
                      >
                        <option value="">Select Quarter</option>
                        {quarterChoices.map(
                          (value: KeyValue, index: number) => {
                            const int = (index + 1).toString();
                            return (
                              <option key={int} value={value.value}>
                                {value.label}
                              </option>
                            );
                          },
                        )}
                      </Select>
                    ) : (
                      <Select
                        inputProps={{ "aria-label": "Select Start Date" }}
                        native
                        labelId={`select-start-date`}
                        id={`select-start-date`}
                        value={formValues.startYear}
                        onChange={handleChange("startYear")}
                        className={classes.selectError}
                        error={
                          parseInt(formValues.startYear) >
                          parseInt(formValues.endYear)
                        }
                      >
                        <option value="">Select Start Date</option>
                        {startYearChoice.map((e: number, index: number) => {
                          const int = (index + 1).toString();

                          return (
                            <option key={int} value={e}>
                              {e}
                            </option>
                          );
                        })}
                      </Select>
                    )}
                  </FormControl>
                  {formValues.startYear &&
                    formValues.endYear &&
                    parseInt(formValues.startYear) >
                      parseInt(formValues.endYear) && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#ffffff",
                          padding: 0,
                        }}
                      >
                        Start date cannot be after end date.
                      </Typography>
                    )}
                </Grid>
                <Grid
                  size={{
                    sm: 3,
                  }}
                >
                  <FormControl
                    variant="outlined"
                    fullWidth
                    size="small"
                    margin="dense"
                    disabled={formValues.reportType === ""}
                  >
                    <Select
                      style={{ marginTop: 24 }}
                      inputProps={{
                        "aria-label":
                          formValues.reportType === "Financial"
                            ? "Select Year"
                            : "Select End Date",
                      }}
                      native
                      labelId={
                        formValues.reportType === "Financial"
                          ? "select-year"
                          : "select-end-date"
                      }
                      id={
                        formValues.reportType === "Financial"
                          ? "select-year"
                          : "select-end-date"
                      }
                      value={formValues.endYear}
                      onChange={handleChange("endYear")}
                      error={
                        parseInt(formValues.startYear) >
                        parseInt(formValues.endYear)
                      }
                      className={classes.selectError}
                    >
                      <option value="">
                        {formValues.reportType === "Financial"
                          ? "Select Year"
                          : "Select End Date"}
                      </option>
                      {endYearChoice.map((e: number, index: number) => {
                        const int = (index + 1).toString();

                        return (
                          <option key={int} value={e}>
                            {e}
                          </option>
                        );
                      })}
                    </Select>
                    {formValues.startYear &&
                      formValues.endYear &&
                      parseInt(formValues.startYear) >
                        parseInt(formValues.endYear) && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#ffffff",
                            padding: 0,
                          }}
                        >
                          End date cannot be before start date.
                        </Typography>
                      )}
                  </FormControl>
                </Grid>
              </>
            )}
            <Grid
              size={{
                sm: 3,
              }}
            >
              <Typography
                className={classes.searchPanelHeadings}
                variant="body2"
              >
                Download Format
              </Typography>
              <FormControl
                variant="outlined"
                fullWidth
                size="small"
                margin="dense"
                disabled={formValues.reportType === ""}
              >
                <Select
                  inputProps={{ "aria-label": "Select Download Format" }}
                  native
                  labelId={`select-download-format`}
                  id={`select-download-format`}
                  value={formValues.format}
                  onChange={handleChange("format")}
                >
                  {[
                    "CSV (comma separated values)",
                    "JSON (JavaScript Object Notation)",
                  ].map((value: string, index: number) => {
                    const int = (index + 1).toString();

                    return (
                      <option key={int} value={value}>
                        {value}
                      </option>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
        <Box
          style={{ width: "100%", height: "80px", backgroundColor: "#d9e8f6" }}
          sx={{
            textAlign: "center",
            padding: 2.5,
          }}
        >
          {dataLoading ? (
            <LoadingIndicator text={" "} />
          ) : (
            <Button
              variant="contained"
              color="primary"
              className={classes.button}
              onClick={() => setDataLoading(true)}
              id="download-data-btn"
              disabled={
                parseInt(formValues.startYear) > parseInt(formValues.endYear) ||
                isFormIncomplete
              }
            >
              Download Selected Data
            </Button>
          )}
        </Box>
        {footer}
        <Grid
          container
          spacing={2}
          sx={{
            marginTop: 1,
          }}
        >
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <Typography variant="h3" style={{ paddingBottom: 0 }}>
              Data Definitions Files
            </Typography>
            <Typography variant="body2">
              Use these definition files to help you understand the bulk data:
            </Typography>
            <List style={{ padding: 0 }}>
              {definitionLinks.map((file, index) => (
                <ListItem style={{ paddingTop: 0 }} key={index}>
                  <ListItemIcon style={{ minWidth: "20px" }}>
                    <FiberManualRecordIcon className={classes.MuiSvgIcon} />
                  </ListItemIcon>
                  <Typography
                    component="span"
                    variant={"body2"}
                    style={{ paddingBottom: 0 }}
                  >
                    <Link href={file.url}>{file.name}</Link>
                    &nbsp;- {file.description}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <Typography variant="h3" style={{ paddingBottom: 0 }}>
              Other Bulk Data Sources
            </Typography>
            <Typography variant="body2">
              For your convenience, we also provide these links to other bulk
              data sources:
            </Typography>
            <List style={{ padding: 0 }}>
              {bulkLinks.map((file, index) => (
                <ListItem style={{ paddingTop: 0 }} key={index}>
                  <ListItemIcon style={{ minWidth: 20 }}>
                    <FiberManualRecordIcon className={classes.MuiSvgIcon} />
                  </ListItemIcon>
                  <Typography
                    component="span"
                    variant={"body2"}
                    style={{ paddingBottom: 0 }}
                  >
                    <Link href={file.url}>{file.name}</Link>
                    {file.description ? ` - ${file.description}` : ""}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </Grid>
        </Grid>
      </AppBody>
    </Root>
  );
}
